import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, GitCompareArrows, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import type { Bootstrap, LookupParams } from '@/lib/bootstrap';
import type { BetlensResult } from '@/types';
import { ApiError, fetchBet, fetchClients, toInput } from '@/features/bet-inspection/lib/api';
import { CompareBar } from './components/CompareBar';
import { CompareHints } from './components/CompareHints';
import { CompareSources } from './components/CompareSources';
import { CompareSummary, LaneLetter } from './components/CompareSummary';
import { makeSide, type BetSide } from './lib/side';

/**
 * Compare — two bets, side by side, diffed.
 *
 * The workflow it serves: a player disputes bet B, so support pulls a known-good
 * bet A of the same game and asks "what's actually different?". Each lane runs
 * its own lookup independently (change one side, keep the other); when both are
 * loaded the diff panes render. Each bet is a full betlens run, so the same
 * caveat as Bet Inspection holds — this is human-driven, one pair at a time.
 */
export function CompareBets({ boot }: { boot: Bootstrap }) {
  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const lookupA = useMutation<BetlensResult, Error, LookupParams>({
    mutationFn: (params) => fetchBet({ api: boot.api!, token: boot.token, input: toInput(params) }),
  });
  const lookupB = useMutation<BetlensResult, Error, LookupParams>({
    mutationFn: (params) => fetchBet({ api: boot.api!, token: boot.token, input: toInput(params) }),
  });

  const sideA = useMemo(() => (lookupA.data ? makeSide(lookupA.data) : null), [lookupA.data]);
  const sideB = useMemo(() => (lookupB.data ? makeSide(lookupB.data) : null), [lookupB.data]);

  const bothLoaded = sideA && sideB;

  return (
    <>
      <CompareBar
        clients={clients.data ?? []}
        clientsLoading={clients.isLoading}
        clientsError={clients.isError}
        loadingA={lookupA.isPending}
        loadingB={lookupB.isPending}
        onLookupA={lookupA.mutate}
        onLookupB={lookupB.mutate}
        api={boot.api ?? ''}
        token={boot.token}
      />

      <div className="grid grid-cols-1 divide-y divide-hairline border-b border-hairline md:grid-cols-2 md:divide-x md:divide-y-0">
        <LaneHeader
          letter="A"
          side={sideA}
          loading={lookupA.isPending}
          error={lookupA.error}
        />
        <LaneHeader
          letter="B"
          side={sideB}
          loading={lookupB.isPending}
          error={lookupB.error}
        />
      </div>

      {bothLoaded ? (
        <main className="rise">
          <CompareSummary a={sideA} b={sideB} />
          <CompareHints a={sideA} b={sideB} />
          <CompareSources a={sideA} b={sideB} />
          <footer className="px-4 py-6 text-center text-[11px] text-text-faint">
            comparing two full betlens runs · every source reports its own state —
            an unreachable source is never shown as empty
          </footer>
        </main>
      ) : (
        <Waiting a={Boolean(sideA)} b={Boolean(sideB)} />
      )}
    </>
  );
}

function LaneHeader({
  letter,
  side,
  loading,
  error,
}: {
  letter: 'A' | 'B';
  side: BetSide | null;
  loading: boolean;
  error: Error | null;
}) {
  return (
    <div className="flex items-center gap-3 bg-surface px-4 py-2.5">
      <LaneLetter letter={letter} />
      {loading ? (
        <span className="inline-flex items-center gap-1.5 font-condensed text-[11px] uppercase tracking-wider text-text-dim">
          <Loader2 size={12} className="animate-spin" /> scanning…
        </span>
      ) : error ? (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-crit">
          <AlertCircle size={13} />
          {error instanceof ApiError && error.status === 403
            ? 'not permitted for this operator'
            : error instanceof ApiError && error.status === 404
              ? 'bet not found'
              : error.message}
        </span>
      ) : side ? (
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="font-mono text-[14px] font-semibold text-text">{side.summary.betId}</span>
          <span className="font-sans text-[12px] text-text-dim">
            {side.summary.gameName ?? side.summary.gameUuid}
          </span>
          <span className="font-mono text-[11px] text-text-faint">
            {side.summary.playServiceId}
          </span>
        </div>
      ) : (
        <span className="font-sans text-[12px] text-text-faint">
          Look up bet {letter} above.
        </span>
      )}
    </div>
  );
}

function Waiting({ a, b }: { a: boolean; b: boolean }) {
  const done = Number(a) + Number(b);
  return (
    <div className="instrument-bg flex min-h-[46vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <GitCompareArrows size={22} className="text-text-faint" />
      <p className="max-w-[440px] text-[13px] leading-relaxed text-text-dim">
        {done === 0
          ? 'Look up two bets to compare them side by side — attributes, the rules each one fires, and which sources answered. Best used with a known-good bet of the same game next to the one under dispute.'
          : 'One bet loaded. Look up the second to see the diff.'}
      </p>
    </div>
  );
}
